import type { Context } from "@opentelemetry/api";
import type {
  ReadableSpan,
  Span,
  SpanProcessor,
} from "@opentelemetry/sdk-trace-base";

/**
 * The XHR instrumentation adds `open`/`send`/`loaded` span events
 * unconditionally — `ignoreNetworkEvents` only gates the resource-timing
 * events. Honeycomb bills each span event as a full event, so every client
 * request costs four events, three of which just restate the span's own
 * start/end timestamps. Terminal failure events (`error`, `abort`,
 * `timeout`) carry real signal and are kept.
 */
const LIFECYCLE_EVENT_NAMES = new Set(["open", "send", "loaded"]);

/**
 * Strips zero-signal XHR lifecycle events from spans before export.
 * Register ahead of the exporting span processor.
 */
export class XhrLifecycleEventFilter implements SpanProcessor {
  onStart(_span: Span, _parentContext: Context): void {}

  onEnd(span: ReadableSpan): void {
    const kept = span.events.filter(
      event => !LIFECYCLE_EVENT_NAMES.has(event.name),
    );
    if (kept.length !== span.events.length) {
      // Mutate in place: downstream exporter processors receive the same
      // span object, so this is what trims the exported payload.
      span.events.length = 0;
      span.events.push(...kept);
    }
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}
